import {
    Text,
    Box,
    BlockStack,
    InlineStack,
    InlineGrid,
    Banner,
    Button,
    Badge,
    Checkbox,
    Spinner,
} from '@shopify/polaris';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import apiClient from '../utils/api';

/**
 * Which of a brand's Shopify locations 2W ships from.
 *
 * What it decides: an order fulfilled only from locations NOT ticked here was shipped by the
 * brand, so 2W charges it no shipping fee. Today that is corrected by hand every month with a
 * Commission Adjustment.
 *
 * The list is read from our mirror of the store's locations, not from Shopify. See
 * apiClient.getShippingLocationOptions for why.
 */

/**
 * Two selections holding the same values, whatever order they arrived in.
 *
 * Copied from OrderSourceSettings rather than shared: the two cards save different fields and
 * a helper moved out to be shared by exactly two callers is a file nobody reads.
 */
const sameSet = (a = [], b = []) => {
    const left = [...(a || [])].sort();
    const right = [...(b || [])].sort();
    return left.length === right.length && left.every((v, i) => v === right[i]);
};

/** Whether the ticks on screen differ from the brand record they were loaded from. */
export const locationsDirty = (brand, selected) =>
    !sameSet(brand?.twShippingLocationIds, selected);

/**
 * Whether to warn that nothing is ticked.
 *
 * Only once the mirror has run and returned something: before that an empty list means
 * nothing yet, which the never-synced banner already covers, and those orders are charged
 * as they always were.
 *
 * Deliberately not narrowed to rows that CAN be ticked. A brand whose every location is
 * deactivated also has nothing ticked, and its orders are also charged nothing - the fact
 * that nobody can act on it from here makes it more worth saying, not less.
 */
export const warnNothingTicked = ({ syncedAt, loadFailed, locations = [], selected = [] }) => (
    Boolean(syncedAt) && !loadFailed && locations.length > 0 && selected.length === 0
);

/**
 * Which ticks a reload should show: the server's, or the ones already on screen.
 *
 * The server changes the selection by itself in two cases - the first mirror ticks every
 * active location, and a later one unticks a location that died - and neither refetches the
 * brand. So a reload has to be able to adopt a change it did not make.
 *
 * It must not adopt over an unsaved edit, which is the same discard the savedKey memo
 * guards against. `lastFromServer` is what the server said last time; if the screen still
 * matches that, nobody has touched it and the newer answer wins. Null on the first load,
 * where there is nothing to protect.
 */
export const adoptServerSelection = (onScreen, lastFromServer, fromServer) => (
    sameSet(onScreen, lastFromServer ?? onScreen) ? fromServer : onScreen
);

/**
 * Whether a location can be newly ticked.
 *
 * Deactivated and deleted locations are still SHOWN -- hiding them makes a warehouse someone
 * knows exists simply absent, with no way to tell a deleted one from a typo. They just cannot
 * be newly chosen, because configuring a dead warehouse is never what anyone meant.
 *
 * An already-ticked row stays interactive whatever its state, which is the exception that
 * matters: disabling a ticked row would lock the tick in permanently, and a ticked deleted
 * location is exactly the one somebody needs to clear.
 */
export const isSelectable = (loc) => loc.isActive && !loc.missingSince && !loc.unknown;

/**
 * What state a row is in, as a badge.
 *
 * Deleted and deactivated are DIFFERENT and were being reported as one. A location Shopify
 * no longer returns at all has been removed; one it returns with isActive false is still
 * there and can be switched back on. Lumping them together told somebody who had deleted one
 * and deactivated another that they had deactivated two.
 *
 * Order matters: a row can be more than one of these, and the first is the strongest claim.
 */
export const rowWarning = (loc) => {
    // Deleted covers both ways a location can be gone: one the mirror holds and Shopify has
    // stopped returning (missingSince), and one ticked against a location the mirror has
    // never held (unknown). They differ in how we found out, not in what the reader can do
    // about it — and the summary has always counted them together, so a separate badge made
    // it read "1 deleted" with no Deleted badge anywhere on screen.
    if (loc.unknown || loc.missingSince) return { label: 'Deleted', tone: 'critical' };
    if (!loc.isActive) return { label: 'Deactivated', tone: 'attention' };
    return null;
};

/**
 * Available locations first, then by name.
 *
 * Sorted on AVAILABILITY, never on what is ticked. Availability does not change when somebody
 * clicks, so no row moves out from under the pointer; sorting on selection would move the row
 * being clicked, which is worse than a little scrolling.
 */
export const byAvailabilityThenName = (a, b) => {
    const rank = (x) => (isSelectable(x) ? 0 : 1);
    return rank(a) - rank(b)
        || String(a.name || a.id).localeCompare(String(b.name || b.id));
};

/**
 * The whole state of the list in one line, for beside the heading.
 *
 * Two axes, separated by the middle dot, because they overlap and a flat list of four counts
 * reads as four disjoint groups: a deactivated location is also either selected or not, so
 * "1 selected, 1 deactivated, 3 not selected" looks like five locations. Grouped, the first
 * half always sums to the total and the second half describes states within it.
 *
 * Zero counts are dropped rather than shown as "0 deleted", which invites the reader to work
 * out whether that is a problem.
 */
export const describeSelection = (locations = [], selected = []) => {
    const total = locations.length;
    if (total === 0) return 'No locations';

    // "not selected" counts only what COULD be selected. A deleted or deactivated location
    // is not an unticked option somebody has yet to consider — it cannot be ticked at all, so
    // counting it there reads as a choice left open when there is none.
    const picked = locations.filter((l) => selected.includes(l.id)).length;
    const open = locations.filter((l) => isSelectable(l) && !selected.includes(l.id)).length;
    const parts = [`${picked} selected`, `${open} not selected`];

    const states = [];
    const deleted = locations.filter((l) => l.missingSince || l.unknown).length;
    const deactivated = locations.filter((l) => !l.isActive && !l.missingSince && !l.unknown).length;
    if (deactivated) states.push(`${deactivated} deactivated`);
    if (deleted) states.push(`${deleted} deleted`);

    return states.length ? `${parts.join(', ')} · ${states.join(', ')}` : parts.join(', ');
};

/**
 * Caps the list height so the card stops growing with the number of locations.
 *
 * Same device as the Order Sources card, at a smaller number because each row here carries an
 * address under the label and so is roughly twice the height of a plain checkbox. At two
 * columns this shows three rows -- six locations -- and scrolls past that, which keeps BoxC
 * Shipping Pricing below it on screen instead of pushed off the bottom.
 */
const SCROLL_CAP = { maxHeight: '176px', overflowY: 'auto' };

/** "19 Oct 2026, 02:14" or null. */
const formatSynced = (value) => {
    if (!value) return null;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString(undefined, {
        day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
};

export const TwShippingLocations = ({
    brand,
    saving,
    setSaving,
    setError,
    fetchBrandData,
    disabled = false,
}) => {
    const [selected, setSelected] = useState(brand?.twShippingLocationIds || []);
    const [locations, setLocations] = useState([]);
    const [syncedAt, setSyncedAt] = useState(null);
    // Worked out by the server, not here: the question "is this one a problem" has one
    // answer, and a second copy of the rule in the console is how two copies drift.
    const [attention, setAttention] = useState({ unreviewed: [], deadTicked: [] });
    // True from the start. It is false for one frame otherwise, and that frame renders the
    // "never synced" warning against an empty list on every single mount.
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [loadFailed, setLoadFailed] = useState(false);
    // Set when a Sync now has been queued and the stamp had not moved by the time we looked.
    const [syncStillRunning, setSyncStillRunning] = useState(false);
    // Held so unmounting cancels the wait below rather than leaving it to fire into nothing.
    const syncTimer = useRef(null);

    useEffect(() => () => {
        if (syncTimer.current) clearTimeout(syncTimer.current);
    }, []);

    /**
     * The ticks as last SAVED, from wherever we last heard it - the brand record on mount,
     * or the server's own answer after a reload adopted one.
     *
     * Kept here rather than read from the brand prop, because the prop only refreshes when
     * the page refetches the brand and a sync does not do that. Comparing against the prop
     * made the card report an unsaved edit for a change that was already in the database:
     * Save lit up, and the note telling somebody to run a Force recalculation appeared, for
     * ticks nobody had touched.
     *
     * The ref is the same value, for the comparison inside a functional state update where
     * reading the state would be stale.
     */
    const savedRef = useRef(null);
    const [savedTicks, setSavedTicks] = useState(() => [...(brand?.twShippingLocationIds || [])]);
    const rememberSaved = useCallback((next) => {
        savedRef.current = next;
        setSavedTicks(next);
    }, []);

    const load = useCallback(async () => {
        if (!brand?.id) return;
        setLoading(true);
        try {
            const options = await apiClient.getShippingLocationOptions(brand.id);
            setLocations(options.locations || []);
            setSyncedAt(options.syncedAt || null);
            setAttention(options.attention || { unreviewed: [], deadTicked: [] });
            setLoadFailed(false);

            // The server can change the ticks on its own: the first mirror ticks every
            // active location. A later one never does - a location that dies keeps its tick
            // and is raised above instead. The mirror does not refetch the brand, so without
            // this the page holds the selection it loaded
            // before the sync - the picker reads "0 selected" while the database says
            // otherwise, and only a browser refresh puts it right.
            //
            // Adopted ONLY when the ticks on screen still match what the server last said.
            // Anything else is an unsaved edit, and a background refresh must not throw it
            // away - that is the same discard the savedKey memo below exists to stop.
            const fromServer = (options.locations || [])
                .filter((l) => l.selected)
                .map((l) => l.id);
            setSelected((prev) => adoptServerSelection(prev, savedRef.current, fromServer));
            rememberSaved(fromServer);
            // Returned so syncNow can tell whether the stamp moved. Undefined on failure,
            // which that caller treats as "cannot say" rather than as "did not run".
            return options;
        } catch (err) {
            // Said on screen rather than only in the console. An empty list and a failed load
            // look identical otherwise, and one of them means "this store has no locations",
            // which is a reason to go and create one.
            console.error('Failed to load shipping location options:', err);
            setLoadFailed(true);
        } finally {
            setLoading(false);
        }
    }, [brand?.id, rememberSaved]);

    useEffect(() => { load(); }, [load]);

    /**
     * The saved selection, as a value that only changes when the CONTENT does.
     *
     * The effect below used to depend on brand.twShippingLocationIds directly. That is an
     * array rebuilt from JSON on every fetchBrandData — nine call sites on this tab — so its
     * identity changed on every refetch whether or not a single id had. Saving an unrelated
     * section, a BoxC price say, therefore reset these ticks and silently discarded edits
     * that had not been saved yet, leaving Save disabled as though there had been nothing to
     * save.
     *
     * Sorted because order carries no meaning here: the list is used for membership tests and
     * the server stores it through a Set.
     */
    const savedKey = useMemo(
        () => JSON.stringify([...(brand?.twShippingLocationIds || [])].sort()),
        [brand?.twShippingLocationIds],
    );

    // Now only when the stored selection really differs — a save here, a save by someone
    // else, or the sync unticking a location that died.
    useEffect(() => {
        const stored = JSON.parse(savedKey);
        setSelected(stored);
        rememberSaved(stored);
    }, [savedKey, rememberSaved]);

    const toggle = (id) => setSelected((prev) => (
        prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]
    ));

    /**
     * Re-mirror now.
     *
     * The run is queued, so it has not finished when this returns -- hence the reload after a
     * short wait rather than straight away, and the wording "Sync queued" rather than "Synced".
     * Claiming it was done when the job has not run is how somebody concludes the store really
     * does have no locations.
     */
    const syncNow = async () => {
        setSyncing(true);
        setSyncStillRunning(false);
        setError?.('');
        // Captured before the run is queued, so the reload below can tell "it ran" from "it
        // has not been picked up yet". Without this the wait is a guess dressed as a result.
        const before = syncedAt ?? null;
        try {
            await apiClient.triggerLocationSync(brand.id);
            syncTimer.current = setTimeout(async () => {
                syncTimer.current = null;
                const options = await load();
                // A failed load answers "cannot say", not "did not run" — claiming the latter
                // would send somebody looking for a worker problem that is really a fetch one.
                setSyncStillRunning(Boolean(options) && (options.syncedAt ?? null) === before);
                setSyncing(false);
            }, 2500);
        } catch (err) {
            setError?.(err.message || 'Failed to queue a location sync');
            setSyncing(false);
        }
    };

    const save = async () => {
        setSaving?.(true);
        setError?.('');
        try {
            await apiClient.updateBrand(brand.id, { twShippingLocationIds: selected });
            await fetchBrandData?.();
        } catch (err) {
            setError?.(err.message || 'Failed to save the 2W shipping locations');
        } finally {
            setSaving?.(false);
        }
    };

    // Every location is shown, whatever its state. Sorted so the usable ones lead.
    const rows = [...locations].sort(byAvailabilityThenName);
    // Against what we last knew to be saved, not against the brand prop - see savedTicks.
    const dirty = !sameSet(savedTicks, selected);
    const neverSynced = !syncedAt;
    const nothingTicked = warnNothingTicked({ syncedAt, loadFailed, locations: rows, selected });

    // Select all operates on what CAN be ticked. A deleted or deactivated location is not
    // part of "all" — it cannot be chosen individually, so sweeping it in would produce a
    // selection nobody could reproduce by hand.
    const selectable = rows.filter(isSelectable);
    const allSelected = selectable.length > 0
        && selectable.every((l) => selected.includes(l.id));
    // Over the SELECTABLE rows, the same set toggleAll acts on. Counting every ticked id
    // instead leaves the control stuck reporting "mixed" for a brand whose only remaining
    // tick is on a location that has died - which is announced as mixed too, not just drawn.
    const someSelected = selectable.some((l) => selected.includes(l.id)) && !allSelected;

    // Clearing drops ONLY the selectable ids, so a tick on a location that has since died is
    // left alone rather than silently cleaned up by a control that says "select all".
    const toggleAll = () => setSelected((prev) => (
        allSelected
            ? prev.filter((id) => !selectable.some((l) => l.id === id))
            : [...new Set([...prev, ...selectable.map((l) => l.id)])]
    ));

    return (
        <BlockStack gap="200">
            <InlineStack align="space-between" blockAlign="center">
                <BlockStack gap="050">
                    {/* align is set EXPLICITLY on every nested InlineStack on this card.
                        Polaris resolves justify-content from --pc-inline-stack-align, and a
                        custom property INHERITS — so a stack with no align inside the
                        space-between one above it silently becomes space-between too, which
                        pushed this heading and its summary to opposite ends of the row. */}
                    <InlineStack align="start" gap="200" blockAlign="center">
                        {/* "Locations/Warehouses" rather than "2W shipping locations": a
                            brand ships from several of our warehouses and sometimes its own,
                            and warehouse is the word used for them everywhere else. Whose
                            they are is carried by the line underneath. */}
                        <Text variant="headingMd" as="h3">Locations/Warehouses</Text>
                        {/* Beside the heading, not under the list: it is the answer to "what
                            is this brand set to" and has to be readable without reading four
                            checkboxes. */}
                        {!loading && locations.length > 0 && (
                            <Text variant="bodySm" tone="subdued">
                                {describeSelection(locations, selected)}
                            </Text>
                        )}
                    </InlineStack>
                    <Text variant="bodyMd" tone="subdued">
                        Select Location/Warehouse for Shipping Charges
                    </Text>
                </BlockStack>
                <InlineStack align="end" gap="200" blockAlign="center">
                    <Text variant="bodySm" tone="subdued">
                        {neverSynced ? 'Never synced' : `Synced ${formatSynced(syncedAt)}`}
                    </Text>
                    <Button
                        onClick={syncNow}
                        loading={syncing}
                        disabled={disabled || syncing || saving}
                    >
                        Sync now
                    </Button>
                    {/* Beside Sync now rather than in a footer row of its own. The two are the
                        only actions on this card and a row holding one right-aligned button
                        cost more vertical space than the list it followed. */}
                    <Button
                        variant="primary"
                        onClick={save}
                        loading={saving}
                        disabled={disabled || saving || !dirty}
                    >
                        Save locations
                    </Button>
                </InlineStack>
            </InlineStack>

            {syncStillRunning && (
                // The run is queued, and nothing here waits for a worker. Saying so beats
                // reloading the same list and letting it read as the answer — which is how
                // somebody concludes the store has no locations when the worker is simply
                // not running.
                <Banner tone="info">
                    Sync queued, but it hasn&rsquo;t finished yet &mdash; the list below is
                    still the previous one. Give it a moment and use Sync now again, or check
                    Sync History if it never completes.
                </Banner>
            )}

            {loadFailed && (
                <Banner tone="critical">
                    The location list could not be loaded. The ticks below are still the saved
                    setting; the list of locations to choose from is missing.
                </Banner>
            )}

            {nothingTicked && (
                // Nothing ticked says none of these warehouses are 2W's, so every order
                // reads as the brand's and carries no fee. Worth saying out loud: it is a
                // whole brand's shipping income, and the list can reach this state from a
                // single click on Select all.
                //
                // The second sentence is the one that stops a panic. Clearing the list does
                // not retroactively credit anything - a fee already charged stays until
                // somebody deliberately recalculates that month.
                <Banner tone="warning">
                    Nothing is ticked, so <strong>no location counts as 2W&rsquo;s</strong> and
                    orders here will be charged no shipping fee &mdash; each one reads as
                    shipped by the brand. Tick the locations 2W ships from.
                    {' '}Fees already charged do not change until somebody runs Force
                    recalculation for that month.
                </Banner>
            )}

            {/* TWO DIFFERENT PROBLEMS, said apart because the fix differs. A location nobody
                has decided about may be one 2W ships from, and until somebody says so its
                orders are billed no shipping. A dead location that is still ticked is being
                charged for right now and may not be 2W's any more. One is a missed charge,
                the other a wrong one. */}
            {!loading && !loadFailed && attention.deadTicked.length > 0 && (
                <Banner tone="warning">
                    {attention.deadTicked.length === 1 ? 'A location' : `${attention.deadTicked.length} locations`}
                    {' '}ticked as 2W&rsquo;s {attention.deadTicked.length === 1 ? 'is' : 'are'} deleted
                    or deactivated in Shopify: <strong>{attention.deadTicked.map((l) => l.name || l.id).join(', ')}</strong>.
                    {' '}The {attention.deadTicked.length === 1 ? 'tick has' : 'ticks have'} been left
                    alone, so this brand is still charged for shipping from
                    {' '}{attention.deadTicked.length === 1 ? 'it' : 'them'} &mdash; unticking
                    automatically would stop billing without anybody deciding to. Untick
                    {' '}{attention.deadTicked.length === 1 ? 'it' : 'them'} if 2W no longer ships from
                    {' '}{attention.deadTicked.length === 1 ? 'there' : 'those'}, or leave
                    {' '}{attention.deadTicked.length === 1 ? 'it' : 'them'} if the warehouse simply moved.
                </Banner>
            )}

            {!loading && !loadFailed && attention.unreviewed.length > 0 && (
                <Banner tone="info">
                    {attention.unreviewed.length === 1 ? 'A new location has' : `${attention.unreviewed.length} new locations have`}
                    {' '}appeared in Shopify since anybody last looked:
                    {' '}<strong>{attention.unreviewed.map((l) => l.name || l.id).join(', ')}</strong>.
                    {' '}{attention.unreviewed.length === 1 ? 'It is' : 'They are'} not ticked, so
                    orders shipped from {attention.unreviewed.length === 1 ? 'it' : 'them'} are
                    charged no shipping fee. Tick {attention.unreviewed.length === 1 ? 'it' : 'them'}
                    {' '}if 2W ships from {attention.unreviewed.length === 1 ? 'there' : 'those'},
                    then Save &mdash; saving records the decision either way and clears this.
                </Banner>
            )}

            {/* Three states a blank list can be in, and they need different answers. */}
            {!loading && !loadFailed && neverSynced && (
                <Banner tone="warning">
                    These locations have never been synced from Shopify. Use Sync now &mdash;
                    the run is queued, so give it a moment. If it stays empty, this store has
                    most likely not approved the location permission yet: someone needs to
                    open the 2W app there. Sync History records the reason either way.
                </Banner>
            )}

            {!loading && !loadFailed && !neverSynced && locations.length === 0 && (
                <Banner tone="warning">
                    Shopify returned no locations for this store.
                </Banner>
            )}


            {loading ? (
                <InlineStack align="start" gap="200" blockAlign="center">
                    <Spinner size="small" />
                    <Text variant="bodySm" tone="subdued">Loading locations&hellip;</Text>
                </InlineStack>
            ) : (
                <BlockStack gap="150">
                    {/* Replaces a warning banner that said "nothing is ticked, so everything
                        is charged". A control states the same thing and can be acted on: all
                        ticked IS "charge every order", which is what a brand starts on, so
                        the state worth showing is how far it has moved from that. */}
                    {selectable.length > 0 && (
                        <Checkbox
                            label="Select all"
                            checked={allSelected ? true : (someSelected ? 'indeterminate' : false)}
                            onChange={toggleAll}
                            disabled={disabled || saving}
                        />
                    )}
                    <div style={SCROLL_CAP}>
                    <InlineGrid columns={{ xs: 1, md: 2 }} gap="150">
                        {rows.map((loc) => {
                            const warning = rowWarning(loc);
                            const ticked = selected.includes(loc.id);
                            // Locked only when it is BOTH unusable and not already ticked.
                            // A ticked dead location must stay clickable or the tick can
                            // never be removed.
                            const locked = !isSelectable(loc) && !ticked;
                            return (
                                <Box key={loc.id}>
                                    <InlineStack align="start" gap="150" blockAlign="start" wrap={false}>
                                        <Checkbox
                                            label={loc.name || loc.id}
                                            checked={ticked}
                                            onChange={() => toggle(loc.id)}
                                            disabled={disabled || saving || locked}
                                            helpText={loc.address || undefined}
                                        />
                                        <Box paddingBlockStart="100">
                                            <InlineStack align="start" gap="100">
                                                {loc.isFulfillmentService && (
                                                    <Badge tone="info">Fulfilment service</Badge>
                                                )}
                                                {warning && (
                                                    <Badge tone={warning.tone}>{warning.label}</Badge>
                                                )}
                                            </InlineStack>
                                        </Box>
                                    </InlineStack>
                                </Box>
                            );
                        })}
                    </InlineGrid>
                    </div>

                </BlockStack>
            )}

            {dirty && (
                // The one thing somebody will get wrong. Changing the ticks does not move a
                // fee that is already on an order: the fee is worked out when the order syncs
                // and frozen on it. Said here rather than discovered when the invoice does not
                // change.
                <Text variant="bodySm" tone="subdued">
                    Saving applies to orders synced from now on. To apply it to this
                    month&rsquo;s existing orders, run Sync &amp; Recalculate with Force
                    recalculation on the Orders tab.
                </Text>
            )}
        </BlockStack>
    );
};

export default TwShippingLocations;
